import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Waves, Send, ArrowRight, Lock, FileText, Shield, Users, MapPin, BarChart3, Compass, ChevronLeft, ShieldCheck, Globe, Server, Eye, AlertTriangle, BadgeCheck, KeyRound, Database, Fingerprint, Plane, Sun, X, Mail, ArrowLeft } from 'lucide-react';
import { getStackApp } from '../utils/stackAuth';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import SEO from '../components/SEO';
import { BetaBadge } from '../components/PageShell';

const API = process.env.REACT_APP_BACKEND_URL;

async function fetchWithRetry(url, options, maxRetries = 3) {
  let lastErr;
  for (let i = 0; i < maxRetries; i++) {
    try {
      const res = await fetch(url, options);
      return res;
    } catch (err) {
      lastErr = err;
      if (i < maxRetries - 1) await new Promise(r => setTimeout(r, 500 * 2 ** i));
    }
  }
  throw lastErr;
}
const DEFAULT_HERO = 'https://static.prod-images.emergentagent.com/jobs/703ba438-d4e3-4e30-afca-0655aaab9328/images/8d7a422d31caefbbee8ceb6557c3464c8177465bb6c0e69d03418fedd9d0ab78.png';
const DEFAULT_HERO_LIGHT = 'https://images.unsplash.com/photo-1750184069333-e1f0fef7c1cd?w=800&h=400&fit=crop&q=80';
const SWELL_AVATAR = 'https://images.unsplash.com/photo-1543002601-b85cd5b8439b?w=80&h=80&fit=crop';

const COMMUNITY_IMG = 'https://images.unsplash.com/photo-1767206106390-111946396767?crop=entropy&cs=srgb&fm=jpg&w=400&h=200&fit=crop';
const CONDITIONS_IMG = 'https://images.unsplash.com/photo-1689731428483-33fddb95481e?crop=entropy&cs=srgb&fm=jpg&w=400&h=200&fit=crop';
const SESSIONS_IMG = 'https://images.unsplash.com/photo-1697707900918-e52573b74090?crop=entropy&cs=srgb&fm=jpg&w=400&h=200&fit=crop';

// ── Travel destinations with hero images ─────────────────────────────
const BEACHES = [
  { id: 'default', label: 'Home', city: null, hero: DEFAULT_HERO },
  { id: 'pipeline', label: 'Pipeline', city: 'Haleiwa', hero: 'https://images.unsplash.com/photo-1616143392557-cb168092a96a?w=800&h=400&fit=crop&q=80' },
  { id: 'malibu', label: 'Malibu', city: 'Malibu', hero: 'https://images.pexels.com/photos/30917310/pexels-photo-30917310.jpeg?auto=compress&cs=tinysrgb&w=800&h=400&fit=crop' },
  { id: 'uluwatu', label: 'Uluwatu', city: 'Uluwatu', hero: 'https://images.pexels.com/photos/34771557/pexels-photo-34771557.jpeg?auto=compress&cs=tinysrgb&w=800&h=400&fit=crop' },
  { id: 'goldcoast', label: 'Gold Coast', city: 'Gold Coast', hero: 'https://images.unsplash.com/photo-1619512078332-13384f9465c2?w=800&h=400&fit=crop&q=80' },
  { id: 'tahiti', label: "Tahiti", city: 'Papeete', hero: 'https://images.pexels.com/photos/9470481/pexels-photo-9470481.jpeg?auto=compress&cs=tinysrgb&w=800&h=400&fit=crop' },
  { id: 'nazare', label: 'Nazare', city: 'Nazare', hero: 'https://images.unsplash.com/photo-1709646138551-4d2135ac03d8?w=800&h=400&fit=crop&q=80' },
];

// ── Theme toggle — only light/dark on landing (no dynamic geo) ───────
function SunglassesIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12c0-2 2-4 5-4h1.5a2 2 0 0 1 2 1.5c.2.8.8 1.5 1.5 1.5s1.3-.7 1.5-1.5A2 2 0 0 1 15.5 8H17c3 0 5 2 5 4s-2 4-5 4h-2c-1.5 0-2.5-1-3-2-.5 1-1.5 2-3 2H7c-3 0-5-2-5-4z" />
    </svg>
  );
}

function LandingThemeToggle({ isDark, setThemeMode }) {
  return (
    <button
      onClick={() => setThemeMode(isDark ? 'light' : 'dark')}
      data-testid="landing-theme-toggle"
      className={`flex items-center justify-center w-8 h-8 rounded-full backdrop-blur-md border transition-all ${
        isDark ? 'bg-black/30 border-white/20 hover:bg-black/40' : 'bg-white/50 border-[#0c3547]/20 hover:bg-white/70'
      }`}
    >
      {isDark
        ? <Sun className="w-3.5 h-3.5 text-amber-300" />
        : <SunglassesIcon className={`w-4 h-4 text-[#0c3547]`} />
      }
    </button>
  );
}

// ── Browser fingerprint ──────────────────────────────────────────────
function generateFingerprint() {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('SurfSwell.ai', 2, 2);
    const canvasData = canvas.toDataURL();
    const raw = [
      canvasData.slice(-50), navigator.userAgent,
      screen.width + 'x' + screen.height, screen.colorDepth,
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      navigator.language, navigator.hardwareConcurrency || '',
    ].join('|');
    let hash = 0;
    for (let i = 0; i < raw.length; i++) {
      hash = ((hash << 5) - hash) + raw.charCodeAt(i);
      hash |= 0;
    }
    return 'fp_' + Math.abs(hash).toString(36);
  } catch {
    return 'fp_' + Math.random().toString(36).slice(2, 14);
  }
}

function getAnonState(fp) {
  try {
    const key = `swell_anon_${fp}`;
    const raw = localStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed.date === new Date().toISOString().slice(0, 10)) return parsed;
    }
  } catch {}
  return { count: 0, date: new Date().toISOString().slice(0, 10) };
}

function setAnonState(fp, state) {
  try { localStorage.setItem(`swell_anon_${fp}`, JSON.stringify(state)); } catch {}
}

// ── Typing text ──────────────────────────────────────────────────────
function TypedText({ text, speed = 18, onDone }) {
  const [displayed, setDisplayed] = useState('');
  const idx = useRef(0);
  const cancelled = useRef(false);
  useEffect(() => {
    idx.current = 0;
    setDisplayed('');
    cancelled.current = false;
    const tick = () => {
      if (cancelled.current) return;
      if (idx.current < text.length) {
        setDisplayed(text.slice(0, idx.current + 1));
        idx.current++;
        setTimeout(tick, speed);
      } else { onDone?.(); }
    };
    setTimeout(tick, speed);
    return () => { cancelled.current = true; };
  }, [text, speed, onDone]);
  return <>{displayed}</>;
}

// ── Feature card ─────────────────────────────────────────────────────
function FeatureCard({ icon: Icon, title, desc, img, accent, tags, onClick }) {
  return (
    <button onClick={onClick} className="w-full text-left group" data-testid={`feature-card-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="rounded-xl overflow-hidden border border-white/10 hover:border-primary/30 transition-all duration-300">
        {img && (
          <div className="h-24 w-full overflow-hidden relative">
            <img src={img} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-bg-paper via-bg-paper/40 to-transparent" />
          </div>
        )}
        <div className={`px-3.5 ${img ? 'pb-3 -mt-4 relative' : 'py-3'}`}>
          <div className="flex items-center gap-2 mb-1">
            <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${accent || 'bg-primary/15'}`}>
              <Icon className="w-3.5 h-3.5 text-primary" />
            </div>
            <span className="text-[12px] font-bold tracking-tight">{title}</span>
          </div>
          <p className="text-[10px] text-muted-foreground leading-relaxed">{desc}</p>
          {tags && (
            <div className="flex gap-1.5 mt-2 flex-wrap">
              {tags.map(t => (
                <span key={t} className="text-[8px] bg-white/5 border border-white/10 rounded-full px-2 py-0.5 text-muted-foreground/70">{t}</span>
              ))}
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

// ── Security badge ───────────────────────────────────────────────────
function SecurityBadge({ icon: Icon, label, color }) {
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${color}`}>
      <Icon className="w-3.5 h-3.5 flex-shrink-0" />
      <span className="text-[11px] font-semibold">{label}</span>
    </div>
  );
}

// ── Full legal content views (in-container, not modal) ───────────────
function TermsContent() {
  const effective = 'February 24, 2026';
  return (
    <div className="space-y-5 text-xs text-muted-foreground leading-relaxed">
      <p className="text-[10px] text-muted-foreground/60">Effective: {effective}</p>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2">1. Agreement to Terms</h2>
        <p>By accessing or using the Swell application ("Service"), operated by <span className="text-white font-medium">Offworld Ventures LLC</span>, located at 2219 Main Street, Santa Monica, California 90405, you agree to be bound by these Terms of Service ("Terms"). If you do not agree, do not use the Service.</p>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2">2. Eligibility</h2>
        <p>You must be at least 13 years old to use the Service. If you are under 18, you must have parental or guardian consent. By using the Service, you represent and warrant that you meet these requirements.</p>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2">3. Account Registration</h2>
        <p>You may register using Google Sign-In. You are responsible for maintaining the security of your account credentials and for all activity that occurs under your account. You must notify us immediately of any unauthorized use.</p>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2">4. Acceptable Use</h2>
        <p>You agree not to:</p>
        <ul className="list-disc ml-4 mt-1 space-y-1">
          <li>Use the Service for any unlawful purpose or in violation of any applicable law</li>
          <li>Submit false, misleading, or fraudulent information (including surf spot data)</li>
          <li>Harass, abuse, or threaten other users</li>
          <li>Attempt to gain unauthorized access to any part of the Service</li>
          <li>Interfere with or disrupt the Service or its infrastructure</li>
          <li>Scrape, crawl, or use automated means to access the Service without written permission</li>
          <li>Upload malicious code, viruses, or harmful data</li>
          <li>Manipulate the gamification system (aura points, referrals, login streaks) through fraudulent activity, bot accounts, or self-referral schemes</li>
          <li>Create multiple accounts to circumvent bans, accumulate rewards, or manipulate leaderboards</li>
        </ul>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2">5. User Content</h2>
        <p>You retain ownership of content you submit (spots, session logs, chat messages). By submitting content, you grant Offworld Ventures LLC a non-exclusive, worldwide, royalty-free license to use, display, and distribute that content within the Service, including on community features such as the activity feed, leaderboards, and trending spots. You are solely responsible for content you submit and represent that it does not violate any third-party rights.</p>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2">6. Community Features</h2>
        <p>The Service includes community features such as the activity feed, leaderboards, trending spots, crew connections, and the Stoke Ripple referral program. By participating in community features:</p>
        <ul className="list-disc ml-4 mt-1 space-y-1">
          <li>You acknowledge that your surf sessions, username, profile picture, surf level, session count, water time, aura points, and login streak may be visible to other users</li>
          <li>You agree to interact respectfully with other community members</li>
          <li>You understand that community-submitted surf spots are user-generated content and may not be accurate or safe</li>
        </ul>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2">7. Gamification, Rewards & Treasure Chest</h2>
        <p>The Service includes a gamification system with aura points, login streaks, the Stoke Ripple referral program, and the Treasure Chest reward system. By participating:</p>
        <ul className="list-disc ml-4 mt-1 space-y-1">
          <li><span className="text-white font-medium">Aura points</span> have no monetary value and cannot be exchanged for cash. They are a measure of engagement within the Service.</li>
          <li><span className="text-white font-medium">Rewards</span> (digital badges, stickers, and physical items) are offered at our sole discretion and are subject to availability.</li>
          <li><span className="text-white font-medium">Physical rewards</span> require a valid shipping address. You are responsible for providing accurate fulfillment information.</li>
          <li><span className="text-white font-medium">Referral rewards</span> (Stoke Ripple) are earned when invited users join and engage with the Service.</li>
        </ul>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2">8. AI Features & Swell Skills</h2>
        <p>The Service includes AI-powered features ("Swell") providing surf coaching, conditions analysis, smart session suggestions, natural language calendar scheduling, and configurable automations ("Swell Skills").</p>
        <p className="mt-2"><span className="text-white font-medium">AI-generated content is informational only and does not constitute professional instruction, medical advice, or safety certification.</span> Always exercise your own judgment in the ocean.</p>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2">9. Third-Party Services</h2>
        <p>The Service integrates with third-party APIs including weather data providers (Windy, Open-Meteo, NOAA, NWS), surf forecast providers (Spitcast), geocoding services (OpenStreetMap/Nominatim), webcam providers, AI services (Google Gemini), and authentication providers (Google). We do not control these services and are not responsible for their availability, accuracy, or practices.</p>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2">10. Intellectual Property</h2>
        <p>The Service, including its design, code, branding, and original content, is owned by Offworld Ventures LLC and protected by copyright, trademark, and other intellectual property laws.</p>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2">11. Termination</h2>
        <p>We may suspend or terminate your account at any time, with or without cause or notice, if we believe you have violated these Terms. Upon termination, your right to use the Service ceases immediately.</p>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2">12. Disclaimer of Warranties</h2>
        <p><span className="text-white font-medium">THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.</span></p>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2">13. Limitation of Liability</h2>
        <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, OFFWORLD VENTURES LLC SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES. OUR TOTAL LIABILITY SHALL NOT EXCEED THE GREATER OF $100 USD OR THE AMOUNT YOU PAID US IN THE 12 MONTHS PRECEDING THE CLAIM.</p>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2">14. Assumption of Risk</h2>
        <p>Surfing and ocean activities are inherently dangerous. <span className="text-white font-medium">You acknowledge that you participate in surfing and related activities at your own risk.</span> The Service provides informational tools only and is not a substitute for personal safety judgment, local knowledge, or professional instruction.</p>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2">15. Indemnification</h2>
        <p>You agree to indemnify, defend, and hold harmless Offworld Ventures LLC from any claims, damages, losses, or expenses arising from your use of the Service.</p>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2">16. Governing Law & Disputes</h2>
        <p>These Terms are governed by the laws of the State of California. Any dispute shall be resolved exclusively in the state or federal courts located in Los Angeles County, California.</p>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2">17. Changes to Terms</h2>
        <p>We may update these Terms at any time. Your continued use after changes constitutes acceptance.</p>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2">18. Contact</h2>
        <p className="mb-2">Questions? Chat with <span className="text-primary font-medium">Swell</span> in the app for support.</p>
        <div className="mt-2 bg-white/3 rounded-xl p-3 space-y-1">
          <p className="text-white font-medium">Offworld Ventures LLC</p>
          <p>2219 Main Street, Santa Monica, CA 90405</p>
        </div>
      </section>
      <section className="border-t border-white/5 pt-4">
        <h2 className="text-sm font-semibold text-white mb-3">Terms Changelog</h2>
        <div className="bg-bg-subtle/40 rounded-xl p-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] text-white/70 font-semibold">v2.0 — February 24, 2026</span>
            <span className="text-[8px] bg-primary/15 text-primary px-1.5 py-0.5 rounded">Current</span>
          </div>
          <ul className="text-[10px] text-muted-foreground/60 space-y-0.5 ml-3 list-disc">
            <li>Added Community Features section (Section 6)</li>
            <li>Added Gamification, Rewards & Treasure Chest section (Section 7)</li>
            <li>Expanded AI section to cover Swell Skills (Section 8)</li>
            <li>Added anti-manipulation clause (Section 4)</li>
            <li>Added Assumption of Risk section (Section 14)</li>
          </ul>
        </div>
      </section>
    </div>
  );
}

function PrivacyContent() {
  const effective = 'February 24, 2026';
  return (
    <div className="space-y-5 text-xs text-muted-foreground leading-relaxed">
      <p className="text-[10px] text-muted-foreground/60">Effective: {effective}</p>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2">1. Introduction</h2>
        <p><span className="text-white font-medium">Offworld Ventures LLC</span> ("we," "us," "our"), located at 2219 Main Street, Santa Monica, California 90405, operates the Swell application ("Service"). This Privacy Policy explains how we collect, use, disclose, and protect your personal information.</p>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2">2. Information We Collect</h2>
        <h3 className="text-xs font-semibold text-white/80 mt-3 mb-1">a. Information You Provide</h3>
        <ul className="list-disc ml-4 space-y-1">
          <li><span className="text-white">Account data:</span> Name, email address, and profile picture (via Google Sign-In)</li>
          <li><span className="text-white">Profile data:</span> Surf level, home break, username</li>
          <li><span className="text-white">User content:</span> Surf session logs, spot submissions, chat messages, crew connections, support tickets</li>
          <li><span className="text-white">Preferences:</span> Wave type, wind, tide, favorite spots, wetsuit thickness, session time</li>
          <li><span className="text-white">Calendar data:</span> Planned sessions, AI-scheduled sessions, and session notes</li>
          <li><span className="text-white">Referral data:</span> Crew invitations, referral links, and referral network relationships (Stoke Ripple)</li>
          <li><span className="text-white">Order data:</span> Shipping address for physical Treasure Chest rewards</li>
        </ul>
        <h3 className="text-xs font-semibold text-white/80 mt-3 mb-1">b. Information Collected Automatically</h3>
        <ul className="list-disc ml-4 space-y-1">
          <li><span className="text-white">Usage data:</span> Pages visited, features used, session frequency</li>
          <li><span className="text-white">Device data:</span> Browser type, operating system, screen resolution</li>
          <li><span className="text-white">Cookies:</span> Session cookies for authentication purposes</li>
          <li><span className="text-white">Engagement metrics:</span> Login streaks, total sessions, water time, aura points</li>
        </ul>
        <h3 className="text-xs font-semibold text-white/80 mt-3 mb-1">c. Information from Third Parties</h3>
        <ul className="list-disc ml-4 space-y-1">
          <li><span className="text-white">Google:</span> Name, email, and profile picture when you sign in</li>
          <li><span className="text-white">Weather & marine APIs:</span> We query third-party weather services using geographic coordinates associated with surf spots to display conditions and forecasts</li>
        </ul>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2">3. How We Use Your Information</h2>
        <ul className="list-disc ml-4 space-y-1">
          <li>Provide, operate, and improve the Service</li>
          <li>Personalize your experience (AI coaching, spot recommendations, smart session suggestions)</li>
          <li>Maintain community features (crew, spots, leaderboards, activity feed)</li>
          <li>Administer gamification (aura points, streaks, Treasure Chest rewards, Stoke Ripple)</li>
          <li>Detect and prevent fraud, abuse, or security incidents</li>
          <li>Comply with legal obligations</li>
        </ul>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2">4. AI & Chat Data</h2>
        <p>Messages you send to Swell are processed by Google's Gemini API. <span className="text-white font-medium">We do not use your chat messages to train AI models.</span> Chat history is stored in your account and can be deleted at any time.</p>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2">5. How We Share Your Information</h2>
        <p>We do <span className="text-white font-medium">not</span> sell your personal information. We may share data with:</p>
        <ul className="list-disc ml-4 mt-1 space-y-1">
          <li><span className="text-white">Service providers:</span> Cloud hosting, AI processing, authentication</li>
          <li><span className="text-white">Other users (community):</span> Username, profile picture, surf level, session data on leaderboards and activity feed</li>
          <li><span className="text-white">Fulfillment partners:</span> Shipping info for physical Treasure Chest rewards</li>
          <li><span className="text-white">Legal compliance:</span> When required by law</li>
        </ul>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2">6. Data Retention</h2>
        <p>We retain your data for as long as your account is active. You may delete chat history at any time. Account deletion requests are processed within 30 days.</p>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2">7. Data Security</h2>
        <p>We implement TLS 1.3 encrypted connections, OAuth 2.0 authentication, AES-256 encryption at rest, role-based access controls, and rate limiting. See our Security page for full details.</p>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2">8. Your Rights</h2>
        <p>Depending on your jurisdiction, you may: access, correct, delete, or port your data; opt out of certain processing; and withdraw consent.</p>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2">9. California Privacy Rights (CCPA/CPRA)</h2>
        <p><span className="text-white font-medium">We do not sell or share personal information for cross-context behavioral advertising.</span> California residents have additional rights under CCPA/CPRA.</p>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2">10. Children's Privacy</h2>
        <p>The Service is not directed to children under 13. We do not knowingly collect personal information from children under 13.</p>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2">11. Cookies</h2>
        <p>We use essential session cookies for authentication. No third-party tracking cookies.</p>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2">12. International Data Transfers</h2>
        <p>Your data may be processed in the United States and other jurisdictions where our service providers operate.</p>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2">13. Contact Us</h2>
        <p className="mb-2">Questions? Chat with <span className="text-primary font-medium">Swell</span> in the app for support.</p>
        <div className="mt-2 bg-white/3 rounded-xl p-3 space-y-1">
          <p className="text-white font-medium">Offworld Ventures LLC</p>
          <p>2219 Main Street, Santa Monica, CA 90405</p>
        </div>
      </section>
      <section className="border-t border-white/5 pt-4">
        <h2 className="text-sm font-semibold text-white mb-3">Policy Changelog</h2>
        <div className="bg-bg-subtle/40 rounded-xl p-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] text-white/70 font-semibold">v2.0 — February 24, 2026</span>
            <span className="text-[8px] bg-primary/15 text-primary px-1.5 py-0.5 rounded">Current</span>
          </div>
          <ul className="text-[10px] text-muted-foreground/60 space-y-0.5 ml-3 list-disc">
            <li>Added community activity feed data sharing</li>
            <li>Added calendar AI scheduling data use</li>
            <li>Added Treasure Chest and referral data</li>
            <li>Updated AI provider to Google Gemini</li>
          </ul>
        </div>
      </section>
    </div>
  );
}

function SecurityContent() {
  const effective = 'February 24, 2026';
  return (
    <div className="space-y-6 text-xs text-muted-foreground leading-relaxed">
      <p className="text-[10px] text-muted-foreground/60">Last updated: {effective}</p>
      <div className="bg-green-500/[0.06] border border-green-500/15 rounded-2xl p-4">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-white mb-1">Your data is protected</p>
            <p className="text-xs text-muted-foreground leading-relaxed">Swell is built with security at its foundation. We implement industry-standard security controls and continuously monitor our infrastructure.</p>
          </div>
        </div>
      </div>
      <section>
        <h2 className="text-sm font-semibold text-white mb-3">Standards Alignment</h2>
        <div className="flex flex-wrap gap-2 mb-3">
          <SecurityBadge icon={BadgeCheck} label="SOC 2 Type II" color="text-blue-400 bg-blue-500/10 border-blue-500/15" />
          <SecurityBadge icon={BadgeCheck} label="ISO 27001" color="text-violet-400 bg-violet-500/10 border-violet-500/15" />
          <SecurityBadge icon={BadgeCheck} label="NIST 800-171" color="text-amber-400 bg-amber-500/10 border-amber-500/15" />
        </div>
        <p className="text-[10px] text-muted-foreground/50 leading-relaxed">Our controls align with SOC 2, ISO/IEC 27001:2022, and NIST SP 800-171 Rev. 2 frameworks.</p>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2">Leadership & Expertise</h2>
        <div className="bg-bg-subtle/60 border border-white/5 rounded-xl p-3">
          <p className="text-xs text-muted-foreground leading-relaxed">Created by a founder holding <span className="text-white font-medium">CISSP</span> and <span className="text-white font-medium">CompTIA SecurityX</span> credentials.</p>
        </div>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2 flex items-center gap-2"><Lock className="w-3.5 h-3.5 text-green-400" /> Encryption</h2>
        <h3 className="text-xs font-semibold text-white/80 mt-3 mb-1">Data in Transit</h3>
        <p>All data is encrypted using <span className="text-white font-medium">TLS 1.3</span> with <span className="text-white font-medium">TLS_AES_256_GCM_SHA384</span> cipher suite.</p>
        <h3 className="text-xs font-semibold text-white/80 mt-3 mb-1">Data at Rest</h3>
        <p>Protected using <span className="text-white font-medium">AES-256 encryption at rest</span> with full-disk encryption on database storage volumes.</p>
        <h3 className="text-xs font-semibold text-white/80 mt-3 mb-1">Key Management</h3>
        <p>Encryption keys managed through infrastructure-level key management with automatic rotation.</p>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2 flex items-center gap-2"><Fingerprint className="w-3.5 h-3.5 text-violet-400" /> Authentication & Access Control</h2>
        <ul className="list-disc ml-4 space-y-1.5">
          <li><span className="text-white font-medium">OAuth 2.0 / OpenID Connect</span> via Google SSO. We never store your Google password.</li>
          <li><span className="text-white font-medium">Secure session management</span> with cryptographically secure tokens and defined expiration.</li>
          <li><span className="text-white font-medium">Role-based access control (RBAC)</span> for administrative functions.</li>
        </ul>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2 flex items-center gap-2"><Server className="w-3.5 h-3.5 text-blue-400" /> Infrastructure Security</h2>
        <ul className="list-disc ml-4 space-y-1.5">
          <li><span className="text-white font-medium">Cloud-native</span> containerized infrastructure within Kubernetes.</li>
          <li><span className="text-white font-medium">DDoS protection</span> via Cloudflare edge network.</li>
          <li><span className="text-white font-medium">Rate limiting</span> on all API endpoints.</li>
          <li><span className="text-white font-medium">Network segmentation</span> with least-privilege access.</li>
        </ul>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2 flex items-center gap-2"><Globe className="w-3.5 h-3.5 text-primary" /> API & Third-Party Security</h2>
        <ul className="list-disc ml-4 space-y-1.5">
          <li>All third-party API calls over HTTPS/TLS. API keys never exposed to client-side code.</li>
          <li>Input validation via Pydantic schema validation to prevent injection attacks.</li>
          <li>AI data (Google Gemini) is not used for model training. Chat history deletable at any time.</li>
        </ul>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2 flex items-center gap-2"><Database className="w-3.5 h-3.5 text-secondary" /> Data Protection</h2>
        <ul className="list-disc ml-4 space-y-1.5">
          <li><span className="text-white font-medium">Data minimization:</span> Only necessary data collected.</li>
          <li><span className="text-white font-medium">Right to deletion:</span> Processed within 30 days (CCPA/GDPR).</li>
          <li><span className="text-white font-medium">Breach notification:</span> Within 72 hours of confirmed discovery.</li>
        </ul>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2 flex items-center gap-2"><Eye className="w-3.5 h-3.5 text-amber-400" /> Monitoring & Incident Response</h2>
        <ul className="list-disc ml-4 space-y-1.5">
          <li>Continuous monitoring with automated anomaly alerts.</li>
          <li>Documented incident response plan with severity levels.</li>
          <li>Tamper-resistant audit trails for forensic analysis.</li>
        </ul>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2 flex items-center gap-2"><AlertTriangle className="w-3.5 h-3.5 text-red-400" /> Vulnerability Management</h2>
        <ul className="list-disc ml-4 space-y-1.5">
          <li>Regular dependency review and CVE patching.</li>
          <li>Code review, input validation, OWASP Top 10 mitigations.</li>
          <li>Responsible disclosure welcome — chat with <span className="text-primary font-medium">Swell</span> to open a support ticket</li>
        </ul>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-white mb-2 flex items-center gap-2"><KeyRound className="w-3.5 h-3.5 text-green-400" /> Your Security Controls</h2>
        <ul className="list-disc ml-4 space-y-1.5">
          <li>Sign out from any device at any time</li>
          <li>Delete your AI chat history at any time</li>
          <li>Request complete account and data deletion</li>
          <li>Review active sessions and connected integrations</li>
        </ul>
      </section>
      <section className="border-t border-white/5 pt-4">
        <p className="text-[10px] text-muted-foreground/40 leading-relaxed">
          <span className="text-white/60 font-medium">Offworld Ventures LLC</span> | 2219 Main Street, Santa Monica, CA 90405<br />
          For security inquiries, chat with <span className="text-white/60">Swell</span> to open a support ticket.
        </p>
      </section>
    </div>
  );
}

// ── Intro sequence ───────────────────────────────────────────────────
const INTRO_STEPS = [
  { type: 'text', text: "Hey! I'm Swell — your surf buddy in the lineup.", delay: 1400 },
  { type: 'text', text: "Here's what you get with SurfSwell:", delay: 800 },
  { type: 'feature', feature: 'community', delay: 400 },
  { type: 'feature', feature: 'conditions', delay: 400 },
  { type: 'feature', feature: 'progress', delay: 600 },
  { type: 'text', text: "Ask me anything about surfing, or tap below to jump in. Check out our Terms, Privacy & Security at the bottom.", delay: 800 },
  { type: 'cta', delay: 400 },
];

// ── Main component ───────────────────────────────────────────────────
export default function Landing({ onLogin }) {
  const { themeMode, setThemeMode, isDark, updateLocationOverride } = useTheme();
  const [messages, setMessages] = useState([]);
  const [phase, setPhase] = useState('loading');
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [legalView, setLegalView] = useState(null); // 'terms' | 'privacy' | 'security' | null
  const [fingerprint] = useState(() => generateFingerprint());
  const [anonState, setAnonStateLocal] = useState(() => getAnonState(generateFingerprint()));
  const [typingDone, setTypingDone] = useState({});
  const [showTyping, setShowTyping] = useState(false);
  const [showTravel, setShowTravel] = useState(false);
  const [activeBeach, setActiveBeach] = useState(BEACHES[0]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const introStep = useRef(0);

  // OTP Login state
  const [loginStep, setLoginStep] = useState(null); // null | 'email' | 'code' | 'verifying'
  const [loginEmail, setLoginEmail] = useState('');
  const [loginCode, setLoginCode] = useState('');
  const [loginError, setLoginError] = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [banData, setBanData] = useState(null);

  const handleBeachSelect = (beach) => {
    setActiveBeach(beach);
    setShowTravel(false);
    if (beach.city) {
      updateLocationOverride({ city: beach.city });
    } else {
      updateLocationOverride(null);
    }
  };

  const isLimitReached = anonState.count >= 3;

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, showTyping, typingDone, scrollToBottom]);

  useEffect(() => {
    const timer = setTimeout(() => setPhase('intro'), 2500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (phase !== 'intro') return;
    function showNext() {
      const i = introStep.current;
      if (i >= INTRO_STEPS.length) { setPhase('ready'); return; }
      const step = INTRO_STEPS[i];
      if (step.type === 'text') {
        setShowTyping(true);
        setTimeout(() => {
          setShowTyping(false);
          setMessages(prev => [...prev, { type: 'text', role: 'assistant', content: step.text, id: `intro-${i}` }]);
          introStep.current = i + 1;
          setTimeout(showNext, step.delay);
        }, 1000);
      } else if (step.type === 'feature') {
        setMessages(prev => [...prev, { type: 'feature', feature: step.feature, id: `feat-${i}` }]);
        introStep.current = i + 1;
        setTimeout(showNext, step.delay);
      } else if (step.type === 'cta') {
        setMessages(prev => [...prev, { type: 'cta', id: `cta-${i}` }]);
        introStep.current = i + 1;
        setTimeout(showNext, step.delay);
      }
    }
    showNext();
  }, [phase]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending || isLimitReached || phase !== 'ready') return;
    setInput('');
    setSending(true);
    setMessages(prev => [...prev, { type: 'text', role: 'user', content: text, id: `user-${Date.now()}` }]);
    setShowTyping(true);
    try {
      const chatHistory = messages
        .filter(m => m.type === 'text' && (m.role === 'user' || m.role === 'assistant'))
        .map(m => ({ role: m.role, content: m.content }));
      const res = await fetch(`${API}/api/chat/anonymous`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, fingerprint, history: chatHistory }),
      });
      if (res.ok) {
        const data = await res.json();
        setShowTyping(false);
        setMessages(prev => [...prev, {
          type: 'text', role: 'assistant', content: data.content, id: `ai-${Date.now()}`,
          buttons: data.buttons && data.buttons.length > 0 ? data.buttons : undefined,
        }]);
        const newState = { count: anonState.count + 1, date: anonState.date };
        if (data.remaining === 0 || data.limit_reached) newState.count = 3;
        setAnonStateLocal(newState);
        setAnonState(fingerprint, newState);
      } else {
        setShowTyping(false);
        setMessages(prev => [...prev, { type: 'text', role: 'assistant', content: "Hmm, something went wrong. Try again?", id: `err-${Date.now()}` }]);
      }
    } catch {
      setShowTyping(false);
      setMessages(prev => [...prev, { type: 'text', role: 'assistant', content: "Connection dropped — mind trying again?", id: `err-${Date.now()}` }]);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleQuickReply = (btnText, msgId) => {
    // Remove buttons from the message that was clicked
    setMessages(prev => prev.map(m => m.id === msgId ? { ...m, buttons: undefined } : m));
    // Set input and send
    setInput(btnText);
    // Use a timeout so the state updates before sending
    setTimeout(() => {
      const fakeInput = btnText;
      setInput('');
      setSending(true);
      setMessages(prev => [...prev, { type: 'text', role: 'user', content: fakeInput, id: `user-${Date.now()}` }]);
      setShowTyping(true);
      const chatHistory = messages
        .filter(m => m.type === 'text' && (m.role === 'user' || m.role === 'assistant'))
        .map(m => ({ role: m.role, content: m.content }));
      // This counts as a follow-up, not a new question — don't increment count
      fetch(`${API}/api/chat/anonymous`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: fakeInput, fingerprint, history: [...chatHistory, { role: 'user', content: fakeInput }] }),
      }).then(async res => {
        if (res.ok) {
          const data = await res.json();
          setShowTyping(false);
          setMessages(prev => [...prev, {
            type: 'text', role: 'assistant', content: data.content, id: `ai-${Date.now()}`,
            buttons: data.buttons && data.buttons.length > 0 ? data.buttons : undefined,
          }]);
          if (data.remaining !== undefined) {
            const newState = { count: 3 - data.remaining, date: anonState.date };
            setAnonStateLocal(newState);
            setAnonState(fingerprint, newState);
          }
        } else {
          setShowTyping(false);
          setMessages(prev => [...prev, { type: 'text', role: 'assistant', content: "Hmm, something went wrong. Try again?", id: `err-${Date.now()}` }]);
        }
      }).catch(() => {
        setShowTyping(false);
        setMessages(prev => [...prev, { type: 'text', role: 'assistant', content: "Connection dropped — mind trying again?", id: `err-${Date.now()}` }]);
      }).finally(() => {
        setSending(false);
        inputRef.current?.focus();
      });
    }, 50);
  };

  const handleLogin = () => {
    setLoginStep('choose');
    setLoginEmail('');
    setLoginCode('');
    setLoginError(null);
  };

  const handleOAuthLogin = async (provider) => {
    setLoginLoading(true);
    setLoginError(null);
    try {
      const app = getStackApp();
      console.log('[SurfSwell] Starting OAuth with:', provider, 'project:', process.env.REACT_APP_STACK_PROJECT_ID);
      app.signInWithOAuth(provider);
      // signInWithOAuth triggers a redirect — if we're still here after a delay, something went wrong
      setTimeout(() => {
        setLoginError(`Sign-in redirect didn't happen. Please try again or use email.`);
        setLoginLoading(false);
      }, 5000);
    } catch (err) {
      console.error('OAuth error:', err);
      setLoginError(`Could not start ${provider} sign-in: ${err.message || 'Unknown error'}. Please try email instead.`);
      setLoginLoading(false);
    }
  };

  const handleSendCode = async () => {
    const email = loginEmail.trim().toLowerCase();
    if (!email) return;
    setLoginLoading(true);
    setLoginError(null);
    try {
      const res = await fetchWithRetry(`${API}/api/auth/send-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        setLoginStep('code');
      } else {
        const data = await res.json().catch(() => ({}));
        setLoginError(data.detail || 'Failed to send code. Try again.');
      }
    } catch (err) {
      console.error('Send code error:', err);
      setLoginError('Could not reach server. Check your connection and try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    const code = loginCode.trim();
    if (!code) return;
    setLoginLoading(true);
    setLoginError(null);
    setLoginStep('verifying');
    try {
      const res = await fetchWithRetry(`${API}/api/auth/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      if (res.ok) {
        const userData = await res.json();
        if (userData.is_banned) {
          setBanData(userData);
          setLoginStep(null);
          setLoginLoading(false);
          return;
        }
        if (userData.session_token) {
          localStorage.setItem('swell_session_token', userData.session_token);
        }
        if (onLogin) onLogin(userData);
      } else {
        const data = await res.json().catch(() => ({}));
        setLoginError(data.detail || 'Invalid or expired code.');
        setLoginStep('code');
      }
    } catch (err) {
      console.error('Verify code error:', err);
      setLoginError('Could not reach server. Check your connection and try again.');
      setLoginStep('code');
    } finally {
      setLoginLoading(false);
    }
  };

  const markTypingDone = useCallback((id) => {
    setTypingDone(prev => ({ ...prev, [id]: true }));
  }, []);

  const renderMessage = (msg) => {
    if (msg.type === 'cta') {
      return (
        <motion.div key={msg.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="flex justify-start">
          <div className="w-full" data-testid="landing-login-cta">
            {loginStep === 'choose' ? (
              <div className="bg-primary/10 border border-primary/30 rounded-2xl p-4 space-y-3" data-testid="login-choose-method">
                <div className="flex items-center gap-2 mb-1">
                  <Waves className="w-4 h-4 text-primary" />
                  <span className="text-[13px] font-bold text-primary">Sign in to SurfSwell</span>
                </div>
                <button
                  data-testid="login-google-btn"
                  onClick={() => handleOAuthLogin('google')}
                  disabled={loginLoading}
                  className="w-full flex items-center gap-3 bg-white text-gray-800 font-medium text-sm py-2.5 px-4 rounded-xl hover:bg-gray-50 transition-colors border border-gray-200"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                  Continue with Google
                </button>
                <button
                  data-testid="login-microsoft-btn"
                  onClick={() => handleOAuthLogin('microsoft')}
                  disabled={loginLoading}
                  className="w-full flex items-center gap-3 bg-white text-gray-800 font-medium text-sm py-2.5 px-4 rounded-xl hover:bg-gray-50 transition-colors border border-gray-200"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24"><rect fill="#F25022" x="1" y="1" width="10" height="10"/><rect fill="#00A4EF" x="1" y="13" width="10" height="10"/><rect fill="#7FBA00" x="13" y="1" width="10" height="10"/><rect fill="#FFB900" x="13" y="13" width="10" height="10"/></svg>
                  Continue with Microsoft
                </button>
                <div className="flex items-center gap-2 my-1">
                  <div className="flex-1 h-px bg-white/10"></div>
                  <span className="text-[10px] text-muted-foreground/50">or</span>
                  <div className="flex-1 h-px bg-white/10"></div>
                </div>
                <button
                  data-testid="login-email-btn"
                  onClick={() => setLoginStep('email')}
                  disabled={loginLoading}
                  className="w-full flex items-center gap-3 bg-white/5 text-foreground font-medium text-sm py-2.5 px-4 rounded-xl hover:bg-white/10 transition-colors border border-white/10"
                >
                  <Mail className="w-4 h-4" />
                  Continue with Email
                </button>
                {loginError && <p className="text-[10px] text-red-400" data-testid="login-error">{loginError}</p>}
                <button
                  data-testid="login-back-btn"
                  onClick={() => { setLoginStep(null); setLoginError(null); }}
                  className="w-full text-[10px] text-muted-foreground/50 hover:text-muted-foreground transition-colors py-1"
                >
                  Back
                </button>
              </div>
            ) : loginStep === 'email' ? (
              <div className="bg-primary/10 border border-primary/30 rounded-2xl p-4 space-y-3" data-testid="login-email-form">
                <div className="flex items-center gap-2 mb-1">
                  <Mail className="w-4 h-4 text-primary" />
                  <span className="text-[13px] font-bold text-primary">Sign in with email</span>
                </div>
                <p className="text-[10px] text-muted-foreground">Enter your email and we'll send you a sign-in code.</p>
                <input
                  data-testid="login-email-input"
                  type="email"
                  value={loginEmail}
                  onChange={e => setLoginEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSendCode()}
                  placeholder="you@example.com"
                  autoFocus
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-primary/50 transition-colors placeholder:text-muted-foreground/30"
                />
                {loginError && <p className="text-[10px] text-red-400" data-testid="login-error">{loginError}</p>}
                <div className="flex gap-2">
                  <button
                    data-testid="login-back-to-choose-btn"
                    onClick={() => { setLoginStep('choose'); setLoginError(null); }}
                    className="px-3 py-2 text-[11px] text-muted-foreground hover:text-foreground transition-colors rounded-xl border border-white/10"
                  >
                    <ArrowLeft className="w-3 h-3 inline mr-1" />Back
                  </button>
                  <button
                    data-testid="login-send-code-btn"
                    onClick={handleSendCode}
                    disabled={loginLoading || !loginEmail.trim()}
                    className="flex-1 bg-primary text-black font-bold text-sm py-2 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-40"
                  >
                    {loginLoading ? 'Sending...' : 'Send Code'}
                  </button>
                </div>
              </div>
            ) : loginStep === 'code' ? (
              <div className="bg-primary/10 border border-primary/30 rounded-2xl p-4 space-y-3" data-testid="login-code-form">
                <div className="flex items-center gap-2 mb-1">
                  <KeyRound className="w-4 h-4 text-primary" />
                  <span className="text-[13px] font-bold text-primary">Enter your code</span>
                </div>
                <p className="text-[10px] text-muted-foreground">Check your email ({loginEmail}) for a sign-in code.</p>
                <input
                  data-testid="login-code-input"
                  type="text"
                  value={loginCode}
                  onChange={e => setLoginCode(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleVerifyCode()}
                  placeholder="Enter code"
                  autoFocus
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-primary/50 transition-colors placeholder:text-muted-foreground/30 text-center tracking-widest text-lg"
                />
                {loginError && <p className="text-[10px] text-red-400" data-testid="login-error">{loginError}</p>}
                <div className="flex gap-2">
                  <button
                    data-testid="login-back-to-email-btn"
                    onClick={() => { setLoginStep('email'); setLoginCode(''); setLoginError(null); }}
                    className="px-3 py-2 text-[11px] text-muted-foreground hover:text-foreground transition-colors rounded-xl border border-white/10"
                  >
                    <ArrowLeft className="w-3 h-3 inline mr-1" />Back
                  </button>
                  <button
                    data-testid="login-verify-code-btn"
                    onClick={handleVerifyCode}
                    disabled={loginLoading || !loginCode.trim()}
                    className="flex-1 bg-primary text-black font-bold text-sm py-2 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-40"
                  >
                    {loginLoading ? 'Verifying...' : 'Verify & Sign In'}
                  </button>
                </div>
                <button
                  onClick={handleSendCode}
                  disabled={loginLoading}
                  className="w-full text-[10px] text-muted-foreground/50 hover:text-muted-foreground transition-colors py-1"
                  data-testid="login-resend-code-btn"
                >
                  Didn't receive it? Resend code
                </button>
              </div>
            ) : loginStep === 'verifying' ? (
              <div className="bg-primary/10 border border-primary/30 rounded-2xl p-6 text-center" data-testid="login-verifying">
                <div className="flex space-x-2 justify-center mb-3">
                  <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
                  <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
                  <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
                </div>
                <p className="text-xs text-muted-foreground">Signing you in...</p>
              </div>
            ) : (
              <button onClick={handleLogin} className="w-full group relative overflow-hidden rounded-2xl transition-all duration-300" data-testid="landing-get-started-btn">
                <div className="bg-primary/10 hover:bg-primary/15 border border-dashed border-primary/40 rounded-2xl p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Waves className="w-5 h-5 text-primary" />
                  </div>
                  <div className="text-left flex-1 min-w-0">
                    <p className="text-[13px] font-bold text-primary">Login or Get Started</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Beta is FREE — No credit card needed.</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-primary/60 group-hover:text-primary group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                </div>
              </button>
            )}
          </div>
        </motion.div>
      );
    }
    if (msg.type === 'feature') {
      const cards = {
        community: { icon: Users, title: 'Community & Crew', desc: 'Climb leaderboards, earn aura points, build your crew, and share your stoke. Surfing is better with friends.', img: COMMUNITY_IMG, accent: 'bg-secondary/15', tags: ['Leaderboards', 'Aura Points', 'Referrals', 'Crew'] },
        conditions: { icon: Compass, title: 'Live Conditions & Spots', desc: 'Real-time swell, wind, tide, and wave data for your favorite breaks. Know before you go.', img: CONDITIONS_IMG, accent: 'bg-blue-500/15', tags: ['Swell', 'Wind', 'Tides', 'Forecasts', 'Webcams'] },
        progress: { icon: BarChart3, title: 'Track Sessions & Progress', desc: 'Log every session, track your water time, manage your board garage, and watch yourself improve.', img: SESSIONS_IMG, accent: 'bg-green-500/15', tags: ['Sessions', 'Board Garage', 'Streaks', 'Trips'] },
      };
      const c = cards[msg.feature];
      if (!c) return null;
      return (
        <motion.div key={msg.id} initial={{ opacity: 0, y: 10, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.3 }} className="pl-8">
          <FeatureCard {...c} onClick={handleLogin} />
        </motion.div>
      );
    }
    if (msg.role === 'user') {
      return (
        <motion.div key={msg.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }} className="flex justify-end">
          <div data-testid="landing-chat-user-msg" className="max-w-[80%] px-3.5 py-2.5 text-[13px] leading-relaxed bg-primary text-black rounded-2xl rounded-tr-md">{msg.content}</div>
        </motion.div>
      );
    }
    // Assistant text with optional quick-reply buttons
    return (
      <div key={msg.id}>
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }} className="flex justify-start gap-2">
          <div className="w-6 h-6 rounded-full overflow-hidden border border-white/10 flex-shrink-0 mt-1">
            <img src={SWELL_AVATAR} alt="Swell" className="w-full h-full object-cover" />
          </div>
          <div data-testid="landing-chat-assistant-msg" className="max-w-[80%] px-3.5 py-2.5 text-[13px] leading-relaxed bg-white/5 rounded-2xl rounded-tl-md border border-white/5">
            {typingDone[msg.id] ? msg.content : <TypedText text={msg.content} speed={15} onDone={() => markTypingDone(msg.id)} />}
          </div>
        </motion.div>
        {msg.buttons && msg.buttons.length > 0 && typingDone[msg.id] && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, delay: 0.15 }}
            className="flex flex-wrap gap-1.5 mt-2 pl-8"
            data-testid="quick-reply-buttons"
          >
            {msg.buttons.map((btn) => (
              <button
                key={btn}
                onClick={() => handleQuickReply(btn, msg.id)}
                disabled={sending || isLimitReached}
                data-testid={`quick-reply-${btn.toLowerCase().replace(/\s/g, '-')}`}
                className="px-3 py-1.5 text-[11px] font-medium bg-primary/10 hover:bg-primary/20 border border-primary/30 hover:border-primary/50 text-primary rounded-full transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                {btn}
              </button>
            ))}
          </motion.div>
        )}
      </div>
    );
  };

  // ── Legal pages render inside the same container ────────────────
  const legalTitles = { terms: 'Terms of Service', privacy: 'Privacy Policy', security: 'Security' };

  if (legalView) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-0 py-0 md:px-4 md:py-6 relative z-10">
        <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25 }} className="w-full max-w-none md:max-w-md">
          <div className="bg-bg-paper/95 backdrop-blur-xl rounded-none md:rounded-3xl border-0 md:border md:border-white/10 md:shadow-2xl overflow-hidden flex flex-col chat-container">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/5 flex-shrink-0">
              <button data-testid={`legal-back-btn-${legalView}`} onClick={() => setLegalView(null)} className="p-1.5 hover:bg-white/10 rounded-full transition-colors">
                <ChevronLeft className="w-5 h-5 text-muted-foreground" />
              </button>
              <h1 className="font-heading text-base font-semibold">{legalTitles[legalView]}</h1>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-5">
              {legalView === 'terms' && <TermsContent />}
              {legalView === 'privacy' && <PrivacyContent />}
              {legalView === 'security' && <SecurityContent />}
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-0 py-0 md:px-4 md:py-6 relative z-10">
      <SEO title="SurfSwell.ai — Your AI Surf Coach" description="Meet Swell, your AI surf coach. Get personalized coaching, live conditions, session tracking, and connect with your crew." path="/" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-none md:max-w-md">
        <div className="bg-bg-paper/95 backdrop-blur-xl rounded-none md:rounded-3xl border-0 md:border md:border-white/10 md:shadow-2xl overflow-hidden flex flex-col chat-container">
          {/* Hero — taller, dynamic, with subtle CSS animation */}
          <div className="relative h-52 w-full overflow-hidden flex-shrink-0">
            <AnimatePresence mode="wait">
              <motion.img
                key={`${activeBeach.id}-${isDark}`}
                src={activeBeach.id === 'default' ? (isDark ? DEFAULT_HERO : DEFAULT_HERO_LIGHT) : activeBeach.hero}
                alt={activeBeach.label}
                initial={{ opacity: 0, scale: 1.05 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.6 }}
                className="w-full h-full object-cover object-center landing-hero-drift"
                data-testid="landing-hero-img"
              />
            </AnimatePresence>
            {/* Bottom fade to bg-paper */}
            <div className="absolute inset-0 bg-gradient-to-t from-bg-paper via-transparent to-transparent" />
            {/* Top scrim for contrast on all hero images */}
            <div className={`absolute inset-x-0 top-0 h-20 ${isDark ? 'bg-gradient-to-b from-black/50 to-transparent' : 'bg-gradient-to-b from-white/70 to-transparent'}`} />

            {/* Top bar — branding + Travel + Theme */}
            <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 pt-3.5 pb-6 z-10">
              <div className="flex items-center gap-2" style={{ textShadow: isDark ? '0 1px 4px rgba(0,0,0,0.5)' : '0 1px 4px rgba(255,255,255,0.5)' }}>
                <Waves className={`w-5 h-5 ${isDark ? 'text-white' : 'text-[#0c3547]'}`} />
                <span className={`text-sm font-bold tracking-wide ${isDark ? 'text-white' : 'text-[#0c3547]'}`}>SurfSwell.ai</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowTravel(!showTravel)}
                  data-testid="landing-travel-btn"
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[10px] font-semibold backdrop-blur-md border transition-all ${
                    activeBeach.id !== 'default'
                      ? 'bg-primary/30 border-primary/50 text-white'
                      : isDark
                        ? 'bg-black/30 border-white/20 text-white/90 hover:text-white'
                        : 'bg-white/50 border-[#0c3547]/20 text-[#0c3547] hover:bg-white/70'
                  }`}
                >
                  <Plane className="w-3 h-3" />
                  {activeBeach.id !== 'default' ? activeBeach.label : 'Travel'}
                </button>
                <LandingThemeToggle isDark={isDark} setThemeMode={setThemeMode} />
              </div>
            </div>

            {/* Travel picker dropdown */}
            <AnimatePresence>
              {showTravel && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                  className="absolute top-14 right-4 left-4 z-20 bg-bg-paper/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl p-2.5"
                  data-testid="landing-travel-picker"
                >
                  <div className="flex flex-wrap gap-1.5">
                    {BEACHES.map(b => (
                      <button
                        key={b.id}
                        onClick={() => handleBeachSelect(b)}
                        data-testid={`travel-beach-${b.id}`}
                        className={`px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-all border ${
                          activeBeach.id === b.id
                            ? 'bg-primary/15 border-primary/40 text-primary'
                            : 'bg-white/5 border-white/10 text-muted-foreground hover:bg-white/10 hover:text-foreground'
                        }`}
                      >
                        {b.label}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 -mt-2" data-testid="landing-chat-messages">
            {phase === 'loading' && (
              <div className="flex items-center justify-center h-full">
                <div className="flex space-x-1.5">
                  <div className="w-1.5 h-1.5 bg-primary/40 rounded-full typing-dot"></div>
                  <div className="w-1.5 h-1.5 bg-primary/40 rounded-full typing-dot"></div>
                  <div className="w-1.5 h-1.5 bg-primary/40 rounded-full typing-dot"></div>
                </div>
              </div>
            )}
            {messages.map(renderMessage)}
            {showTyping && (
              <div className="flex justify-start gap-2">
                <div className="w-6 h-6 rounded-full overflow-hidden border border-white/10 flex-shrink-0 mt-1">
                  <img src={SWELL_AVATAR} alt="Swell" className="w-full h-full object-cover" />
                </div>
                <div className="bg-white/5 rounded-2xl rounded-tl-md border border-white/5 px-4 py-3">
                  <div className="flex space-x-1.5">
                    <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full typing-dot"></div>
                    <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full typing-dot"></div>
                    <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full typing-dot"></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          <div className="border-t border-white/5 p-3 flex-shrink-0">
            {isLimitReached ? (
              loginStep ? (
                <div className="space-y-2">
                  {loginStep === 'choose' && (
                    <div className="space-y-2" data-testid="bottom-login-choose">
                      <button onClick={() => handleOAuthLogin('google')} disabled={loginLoading}
                        className="w-full flex items-center justify-center gap-2 bg-white text-gray-800 font-medium text-sm py-2.5 rounded-xl hover:bg-gray-50 transition-colors border border-gray-200" data-testid="bottom-google-btn">
                        <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                        Google
                      </button>
                      <button onClick={() => handleOAuthLogin('microsoft')} disabled={loginLoading}
                        className="w-full flex items-center justify-center gap-2 bg-white text-gray-800 font-medium text-sm py-2.5 rounded-xl hover:bg-gray-50 transition-colors border border-gray-200" data-testid="bottom-microsoft-btn">
                        <svg className="w-4 h-4" viewBox="0 0 24 24"><rect fill="#F25022" x="1" y="1" width="10" height="10"/><rect fill="#00A4EF" x="1" y="13" width="10" height="10"/><rect fill="#7FBA00" x="13" y="1" width="10" height="10"/><rect fill="#FFB900" x="13" y="13" width="10" height="10"/></svg>
                        Microsoft
                      </button>
                      <button onClick={() => setLoginStep('email')}
                        className="w-full text-[10px] text-muted-foreground/50 hover:text-muted-foreground transition-colors py-1" data-testid="bottom-email-link">
                        or sign in with email
                      </button>
                      {loginError && <p className="text-[10px] text-red-400">{loginError}</p>}
                    </div>
                  )}
                  {loginStep === 'email' && (
                    <div className="space-y-2" data-testid="bottom-login-email-form">
                      <input
                        type="email" value={loginEmail}
                        onChange={e => setLoginEmail(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleSendCode()}
                        placeholder="Enter your email"
                        autoFocus
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-primary/50 transition-colors placeholder:text-muted-foreground/30"
                        data-testid="bottom-login-email-input"
                      />
                      {loginError && <p className="text-[10px] text-red-400">{loginError}</p>}
                      <button onClick={handleSendCode} disabled={loginLoading || !loginEmail.trim()}
                        className="w-full bg-primary text-black font-bold text-sm py-2.5 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-40"
                        data-testid="bottom-login-send-btn">
                        {loginLoading ? 'Sending...' : 'Send Sign-in Code'}
                      </button>
                    </div>
                  )}
                  {loginStep === 'code' && (
                    <div className="space-y-2" data-testid="bottom-login-code-form">
                      <p className="text-[10px] text-muted-foreground text-center">Code sent to {loginEmail}</p>
                      <input
                        type="text" value={loginCode}
                        onChange={e => setLoginCode(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleVerifyCode()}
                        placeholder="Enter code"
                        autoFocus
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-primary/50 transition-colors placeholder:text-muted-foreground/30 text-center tracking-widest"
                        data-testid="bottom-login-code-input"
                      />
                      {loginError && <p className="text-[10px] text-red-400">{loginError}</p>}
                      <button onClick={handleVerifyCode} disabled={loginLoading || !loginCode.trim()}
                        className="w-full bg-primary text-black font-bold text-sm py-2.5 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-40"
                        data-testid="bottom-login-verify-btn">
                        {loginLoading ? 'Verifying...' : 'Verify & Sign In'}
                      </button>
                    </div>
                  )}
                  {loginStep === 'verifying' && (
                    <div className="text-center py-3">
                      <div className="flex space-x-2 justify-center mb-2">
                        <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
                        <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
                        <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
                      </div>
                      <p className="text-xs text-muted-foreground">Signing you in...</p>
                    </div>
                  )}
                </div>
              ) : (
                <button onClick={handleLogin} data-testid="landing-login-to-continue-btn"
                  className="w-full bg-primary/10 hover:bg-primary/20 border border-primary/30 text-primary text-[13px] font-semibold py-3 rounded-2xl transition-all inline-flex items-center justify-center gap-2">
                  <Mail className="w-3.5 h-3.5" /> Login to continue chatting
                </button>
              )
            ) : (
              <div className="flex items-end gap-2">
                <div className="flex-1 bg-white/5 border border-white/10 rounded-2xl px-3.5 py-2.5">
                  <input data-testid="landing-chat-input" ref={inputRef} type="text" value={input}
                    onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown}
                    placeholder={phase === 'ready' ? 'Ask me anything about surfing...' : ''}
                    disabled={phase !== 'ready' || sending}
                    className="w-full bg-transparent outline-none text-[13px] placeholder:text-muted-foreground/40 disabled:opacity-40" />
                </div>
                <button data-testid="landing-chat-send-btn" onClick={handleSend}
                  disabled={!input.trim() || sending || phase !== 'ready'}
                  className="p-2.5 bg-primary text-black rounded-full hover:opacity-90 transition-opacity disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0">
                  <Send className="w-4 h-4" />
                </button>
              </div>
            )}
            {!isLimitReached && phase === 'ready' && (
              <p className="text-[10px] text-muted-foreground/30 text-center mt-2">
                {3 - anonState.count} free question{3 - anonState.count !== 1 ? 's' : ''} remaining today
              </p>
            )}
            <div className="flex items-center justify-center gap-3 mt-2 pb-1">
              <button onClick={() => setLegalView('terms')} className="text-[10px] text-muted-foreground/40 hover:text-muted-foreground transition-colors" data-testid="landing-terms-link">
                <FileText className="w-3 h-3 inline mr-0.5 -mt-0.5" /> Terms
              </button>
              <span className="text-muted-foreground/20 text-[10px]">&middot;</span>
              <button onClick={() => setLegalView('privacy')} className="text-[10px] text-muted-foreground/40 hover:text-muted-foreground transition-colors" data-testid="landing-privacy-link">
                <Lock className="w-3 h-3 inline mr-0.5 -mt-0.5" /> Privacy
              </button>
              <span className="text-muted-foreground/20 text-[10px]">&middot;</span>
              <button onClick={() => setLegalView('security')} className="text-[10px] text-muted-foreground/40 hover:text-muted-foreground transition-colors" data-testid="landing-security-link">
                <Shield className="w-3 h-3 inline mr-0.5 -mt-0.5" /> Security
              </button>
            </div>
            <BetaBadge />
          </div>
        </div>
      </motion.div>
    </div>
  );
}
